<template>
  <ProjectDiagram
    :network.sync="diagramNetwork"
    :options="{ showLabels: showLabels }"
    :show-labels="showLabels"
    :allowDrag="!useNodesCoordinates"
    @update-breakers="updateBreakers"
    @click-switch="updateSwitch"
  />
</template>

<script>
export default {
  name: "VisDiagram",
  components: {
    ProjectDiagram: () => import("@/components/ProjectDiagram.vue"),
  },
  mixins: [],
  props: {
    currentCase: { type: Object, required: false, default: () => ({}) },
    useNodesCoordinates: { type: Boolean, required: false, default: false },
    showLabels: { type: Boolean, required: false, default: true },
  },
  data() {
    return {};
  },
  computed: {
    windowHeight() {
      return this.$vuetify.breakpoint.height;
    },
    diagramHeight() {
      return (this.windowHeight - 130) * 0.99;
    },
    computedCase: {
      get() {
        return this.currentCase;
      },
      set(_v) {
        this.$emit("change");
      },
    },
    input: {
      get() {
        return this.currentCase ? this.currentCase.input : null;
      },
      set(v) {
        const caseCopy = this.computedCase;
        caseCopy.input = v;
        this.computedCase = caseCopy;
      },
    },
    diagramNetwork: {
      get() {
        if (!this.input) return {};

        const branches = [];
        const transformers = [];
        const switches = [];

        this.input.branches.forEach((branch) => {
          if (branch.node_from && branch.node_to) {
            branches.push({
              id: branch.id,
              name: branch.name,
              fromBus: branch.node_from,
              toBus: branch.node_to,
            });
          }
        });
        this.input.transformers.forEach((transformer) => {
          if (transformer.high_voltage_node && transformer.low_voltage_node) {
            const branchId = branches.length + 1;
            branches.push({
              id: branchId,
              name: transformer.name,
              fromBus: transformer.high_voltage_node,
              toBus: transformer.low_voltage_node,
            });
            transformers.push({
              id: transformer.id,
              branchId,
              info: this.resourceInfoTable("transformer", transformer),
              name: transformer.name,
            });
          }
        });
        this.input.switches.forEach((switchObj) => {
          if (switchObj.bus_id && switchObj.element) {
            const branchId = branches.length + 1;
            branches.push({
              id: branchId,
              name: switchObj.name,
              fromBus: switchObj.bus_id,
              toBus: switchObj.element,
            });
            switches.push({
              id: switchObj.id,
              branchId,
              state: switchObj.closed ? "close" : "open",
            });
          }
        });

        return {
          bus: this.input.nodes.map((v) => ({ id: v.id, name: v.name })),
          busLocation: this.input.nodes
            .filter((v) => v.diagramPosition && v.diagramPosition.x && v.diagramPosition.y)
            .map((v) => ({ busId: v.id, x: v.diagramPosition.x, y: v.diagramPosition.y })),
          branch: branches,
          storages: this.input.storage
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
              color: this.$vuetify.theme.themes.light.storage,
            })),
          loads: this.input.demands
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
              info: this.resourceInfoTable("demand", v),
              color: this.$vuetify.theme.themes.light.demand,
            })),
          markets: this.input.markets
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
              color: this.$vuetify.theme.themes.light.market,
            })),
          generators: this.input.generators
            .filter((v) => v.bus_id)
            .map((v) => ({
              id: v.id,
              name: v.name,
              busId: v.bus_id,
              breaker: v.active ? "close" : "open",
              type: v.type === "THERMAL" ? "THERMAL" : v.source,
              info: this.resourceInfoTable("generator", v),
              color: this.$utils.generatorColor(v.source),
            })),
          transformers,
          switches,
        };
      },
      set(v) {
        const inputCopy = this.input;
        const nodes = [];
        if (!inputCopy.nodes) return;
        inputCopy.nodes.forEach((node) => {
          const diagramBus = v.bus.find((b) => b.id === node.id);
          nodes.push({
            ...node,
            ...(diagramBus ? { diagramPosition: { x: diagramBus.x, y: diagramBus.y } } : {}),
          });
        });
        inputCopy.nodes = nodes;
        this.input = inputCopy;
      },
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    resourceInfoTable(type, resourceObj) {
      if (type === "generator") {
        return [[resourceObj.capacity[0], "MW"]];
      }
      if (type === "demand") {
        const activePower = resourceObj.active_power[0].value;
        const reactivePower = resourceObj.reactive_power[0].value;
        const apparentPower = this.$utils.formatNumberD3(
          Math.sqrt(activePower ** 2 + reactivePower ** 2)
        );
        return [[apparentPower, "MVA"]];
      }
      if (type === "transformer") {
        const { rated_apparant_power, hv_node_rated_voltage, lv_node_rated_voltage } = resourceObj;
        return [
          // eslint-disable-next-line camelcase
          [`${rated_apparant_power} MVA`],
          // eslint-disable-next-line camelcase
          [`${hv_node_rated_voltage}/${lv_node_rated_voltage} kV`],
        ];
      }
      return null;
    },
    updateBreakers($e) {
      const { resourceType, resource, state } = $e;
      const networkKey = {
        market: "markets",
        load: "demands",
        generatorThermal: "generators",
        generatorSolar: "generators",
        generatorHydro: "generators",
        generatorWind: "generators",
        storage: "storages",
      };
      const inputCopy = this.input;
      const resourceCopy = inputCopy[networkKey[resourceType]].find((v) => v.id === resource.id);
      const resourceIndex = inputCopy[networkKey[resourceType]].findIndex(
        (v) => v.id === resource.id
      );
      if (resourceCopy) {
        resourceCopy.active = state !== "open";
        inputCopy[networkKey[resourceType]].splice(resourceIndex, 1, resourceCopy);
      }
      this.input = inputCopy;
      this.$emit("update-breakers");
    },
    updateSwitch($e) {
      const { id, state } = $e.detail;
      const inputCopy = this.input;
      const switchCopy = inputCopy.switches.find((v) => v.id === id);
      const switchIndex = inputCopy.switches.findIndex((v) => v.id === id);
      if (switchCopy) {
        switchCopy.closed = state === "close";
        inputCopy.switches.splice(switchIndex, 1, switchCopy);
      }
      this.input = inputCopy;
    },
  },
};
</script>

<style></style>
